/**
 * Renders multi-segment polyline "flow" arrows on the map. Vertices and
 * midpoints are interactive for the currently selected arrow only. To avoid
 * SVG pointer-event quirks under the atlas' setPointerCapture, hit-testing is
 * delegated to the parent via the existing FoxholeAtlas marker hit-test
 * callbacks; this component only renders.
 */
import type { ArrowPoint, MapArrow } from '@warplan/shared';
import { getArrowColor, getCategoryStroke } from '../lib/category-colors.js';

/** Pseudo-id encoding used over `onMarkerHitTest` / `onMarkerMoved`. */
export const ARROW_BODY_PREFIX = 'arrow:';
export const ARROW_VERTEX_PREFIX = 'arrow-vx:'; // arrow-vx:<id>:<vertexIdx>
export const ARROW_MIDPOINT_PREFIX = 'arrow-mid:'; // arrow-mid:<id>:<segIdx>

export function isArrowSyntheticId(id: string): boolean {
  return (
    id.startsWith(ARROW_BODY_PREFIX) ||
    id.startsWith(ARROW_VERTEX_PREFIX) ||
    id.startsWith(ARROW_MIDPOINT_PREFIX)
  );
}

export interface ParsedArrowId {
  kind: 'body' | 'vertex' | 'midpoint';
  arrowId: string;
  index?: number;
}

export function parseArrowSyntheticId(id: string): ParsedArrowId | null {
  if (id.startsWith(ARROW_VERTEX_PREFIX)) {
    const rest = id.slice(ARROW_VERTEX_PREFIX.length);
    const lastColon = rest.lastIndexOf(':');
    if (lastColon < 0) return null;
    return {
      kind: 'vertex',
      arrowId: rest.slice(0, lastColon),
      index: Number(rest.slice(lastColon + 1)),
    };
  }
  if (id.startsWith(ARROW_MIDPOINT_PREFIX)) {
    const rest = id.slice(ARROW_MIDPOINT_PREFIX.length);
    const lastColon = rest.lastIndexOf(':');
    if (lastColon < 0) return null;
    return {
      kind: 'midpoint',
      arrowId: rest.slice(0, lastColon),
      index: Number(rest.slice(lastColon + 1)),
    };
  }
  if (id.startsWith(ARROW_BODY_PREFIX)) {
    return { kind: 'body', arrowId: id.slice(ARROW_BODY_PREFIX.length) };
  }
  return null;
}

function pointsAttr(points: ReadonlyArray<ArrowPoint>): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

export interface VertexDragInfo {
  arrowId: string;
  vertexIdx: number;
  worldX: number;
  worldY: number;
}

export interface DraftArrow {
  kind: MapArrow['kind'];
  color: string | null;
  arrowheadStart: boolean;
  arrowheadEnd: boolean;
  dashed: boolean;
  points: ArrowPoint[];
  /** Live cursor position used to render the ghost segment. */
  cursor: ArrowPoint | null;
}

/** Live body translation while user drags the arrow as a whole. */
export interface BodyDragInfo {
  arrowId: string;
  deltaX: number;
  deltaY: number;
}

export interface MapArrowsProps {
  arrows: ReadonlyArray<MapArrow>;
  selectedId: string | null;
  /** Inverse of view.scale (clamped) — used for vertex handle sizing. */
  iconInv: number;
  vertexDrag?: VertexDragInfo | null;
  bodyDrag?: BodyDragInfo | null;
  draft?: DraftArrow | null;
}

const DEFAULT_STROKE_PX = 3;
const SELECTED_STROKE_PX = 4;
const VERTEX_PX = 6;
const MIDPOINT_PX = 5;

export function MapArrows({
  arrows,
  selectedId,
  iconInv,
  vertexDrag,
  bodyDrag,
  draft,
}: MapArrowsProps): React.JSX.Element {
  // Apply optimistic vertex drag / body drag to the matching arrow for rendering.
  const renderArrows: MapArrow[] = arrows.map((a) => {
    if (vertexDrag && vertexDrag.arrowId === a.id) {
      const next = a.points.slice();
      const idx = vertexDrag.vertexIdx;
      if (idx >= 0 && idx < next.length) {
        next[idx] = { x: vertexDrag.worldX, y: vertexDrag.worldY };
      }
      return { ...a, points: next };
    }
    if (bodyDrag && bodyDrag.arrowId === a.id) {
      const next = a.points.map((p) => ({
        x: p.x + bodyDrag.deltaX,
        y: p.y + bodyDrag.deltaY,
      }));
      return { ...a, points: next };
    }
    return a;
  });

  const draftColor = draft ? (draft.color ?? getCategoryStroke(draft.kind)) : null;

  return (
    <g data-layer="map-arrows">
      <defs>
        {renderArrows.map((a) => {
          const color = getArrowColor(a);
          return (
            <marker
              key={`mk-${a.id}`}
              id={`arrow-head-${a.id}`}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
              markerUnits="strokeWidth"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
          );
        })}
        {draft && draftColor ? (
          <marker
            id="arrow-head-draft"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={draftColor} />
          </marker>
        ) : null}
      </defs>

      {renderArrows.map((a) => {
        const color = getArrowColor(a);
        const isSelected = a.id === selectedId;
        const strokeWidth = isSelected ? SELECTED_STROKE_PX : DEFAULT_STROKE_PX;
        return (
          <g key={a.id} data-arrow-id={a.id}>
            {/* Wide invisible hit polyline is not needed: hit-testing is done
             *  in JS via onMarkerHitTest. We still render the visible line. */}
            <polyline
              points={pointsAttr(a.points)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={a.dashed ? '10 8' : undefined}
              vectorEffect="non-scaling-stroke"
              markerStart={a.arrowheadStart ? `url(#arrow-head-${a.id})` : undefined}
              markerEnd={a.arrowheadEnd ? `url(#arrow-head-${a.id})` : undefined}
              pointerEvents="none"
              opacity={isSelected ? 1 : 0.95}
            />
            {isSelected ? (
              <>
                {/* Vertex handles (drag / shift-click to remove). */}
                {a.points.map((p, i) => (
                  <circle
                    key={`v-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r={VERTEX_PX * iconInv}
                    fill="#fff"
                    stroke={color}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    pointerEvents="none"
                  />
                ))}
              </>
            ) : null}
          </g>
        );
      })}

      {/* Draft preview while the user is placing points. */}
      {draft && draftColor && draft.points.length > 0 ? (
        <g data-arrow-draft>
          {draft.points.length >= 2 ? (
            <polyline
              points={pointsAttr(draft.points)}
              fill="none"
              stroke={draftColor}
              strokeWidth={DEFAULT_STROKE_PX}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={draft.dashed ? '6 4' : '6 4'}
              vectorEffect="non-scaling-stroke"
              markerStart={draft.arrowheadStart ? 'url(#arrow-head-draft)' : undefined}
              markerEnd={draft.arrowheadEnd ? 'url(#arrow-head-draft)' : undefined}
              pointerEvents="none"
              opacity={0.75}
            />
          ) : null}
          {/* Ghost segment from last placed point to cursor. */}
          {draft.cursor && draft.points.length > 0 ? (
            <line
              x1={draft.points[draft.points.length - 1]!.x}
              y1={draft.points[draft.points.length - 1]!.y}
              x2={draft.cursor.x}
              y2={draft.cursor.y}
              stroke={draftColor}
              strokeWidth={DEFAULT_STROKE_PX}
              strokeLinecap="round"
              strokeDasharray="2 4"
              vectorEffect="non-scaling-stroke"
              opacity={0.5}
              pointerEvents="none"
            />
          ) : null}
          {/* Placed vertex dots. */}
          {draft.points.map((p, i) => (
            <circle
              key={`dp-${i}`}
              cx={p.x}
              cy={p.y}
              r={VERTEX_PX * iconInv}
              fill={draftColor}
              stroke="#fff"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          ))}
        </g>
      ) : null}
    </g>
  );
}

/* ----------------------------------------------------------------------- *
 *  Hit-testing helpers (consumed by MapPage's onMarkerHitTest callback).  *
 * ----------------------------------------------------------------------- */

/** Squared distance from point P to segment AB. */
function distSqToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return ex * ex + ey * ey;
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t * dx;
  const qy = ay + t * dy;
  const ex = px - qx;
  const ey = py - qy;
  return ex * ex + ey * ey;
}

export interface ArrowHitResult {
  syntheticId: string;
}

/**
 * Tests whether the cursor hits an arrow vertex / midpoint (only for the
 * selected arrow), or any arrow's body. Returns a synthetic id encoded for
 * onMarkerHitTest, or null.
 */
export function hitTestArrows(
  arrows: ReadonlyArray<MapArrow>,
  selectedId: string | null,
  worldX: number,
  worldY: number,
  scale: number,
): ArrowHitResult | null {
  // Selected arrow: handles take priority.
  if (selectedId) {
    const sel = arrows.find((a) => a.id === selectedId);
    if (sel) {
      const handleR = (VERTEX_PX + 2) / scale;
      for (let i = 0; i < sel.points.length; i++) {
        const p = sel.points[i]!;
        const dx = p.x - worldX;
        const dy = p.y - worldY;
        if (dx * dx + dy * dy <= handleR * handleR) {
          return { syntheticId: `${ARROW_VERTEX_PREFIX}${sel.id}:${i}` };
        }
      }
      const midR = (MIDPOINT_PX + 2) / scale;
      for (let i = 0; i < sel.points.length - 1; i++) {
        const p = sel.points[i]!;
        const q = sel.points[i + 1]!;
        const mx = (p.x + q.x) / 2;
        const my = (p.y + q.y) / 2;
        const dx = mx - worldX;
        const dy = my - worldY;
        if (dx * dx + dy * dy <= midR * midR) {
          return { syntheticId: `${ARROW_MIDPOINT_PREFIX}${sel.id}:${i}` };
        }
      }
    }
  }
  // Body hits.
  const bodyTol = 8 / scale;
  const bodyTol2 = bodyTol * bodyTol;
  let best: { d2: number; id: string } | null = null;
  for (const a of arrows) {
    for (let i = 0; i < a.points.length - 1; i++) {
      const p = a.points[i]!;
      const q = a.points[i + 1]!;
      const d2 = distSqToSegment(worldX, worldY, p.x, p.y, q.x, q.y);
      if (d2 <= bodyTol2 && (best === null || d2 < best.d2)) {
        best = { d2, id: `${ARROW_BODY_PREFIX}${a.id}` };
      }
    }
  }
  return best ? { syntheticId: best.id } : null;
}
